import { LcAddress } from "@/domains/core/lc-address"
import { PROCESSING_REWARDS_WALLET } from "@/domains/on-chain/constants"
import { flyQuoteResponseSchema, flyTransactionResponseSchema } from "@/domains/validators"

import type { IAggregatorApiTransport } from "../interfaces"
import type { AggregatorQuoteRequest, AggregatorTransactionRequest, RawAggregatorQuote } from "../types"

const FLY_AFFILIATE_FEE_IN_PERCENTAGE = "0"

type FlyConfig = {
  quoteAPI: string
  transactionAPI: string
  nativeAddress: string
  network: string
  affiliateAddress: LcAddress
}

const BASE_CONFIG: FlyConfig = {
  quoteAPI: "https://api.fly.trade/aggregator/quote",
  transactionAPI: "https://api.fly.trade/aggregator/transaction",
  nativeAddress: "0x0000000000000000000000000000000000000000",
  network: "base",
  affiliateAddress: PROCESSING_REWARDS_WALLET,
}

const getFlyChainConfig = (chainId: number): FlyConfig => {
  switch (chainId) {
    case 8453:
      return BASE_CONFIG
    default:
      throw new Error(`Chain ${chainId} not supported by Fly`)
  }
}

/**
 * Fly API Transport
 * Pure data loading - no state, no caching, no side effects
 * Returns raw API responses before normalization
 */
export class FlyApiTransport implements IAggregatorApiTransport {
  /**
   * Fetch quote from Fly API
   * Returns raw response (before normalization)
   */
  async fetchQuote(params: AggregatorQuoteRequest): Promise<RawAggregatorQuote> {
    const flyConfig = getFlyChainConfig(params.chainId)

    // Convert token addresses - Fly uses 0x0 for native token
    const inputTokenAddress = params.inputToken.address.equalsString(params.nativeToken.address.toString())
      ? flyConfig.nativeAddress
      : params.inputToken.address.toViemAddress()

    const outputTokenAddress = params.outputToken.address.equalsString(params.nativeToken.address.toString())
      ? flyConfig.nativeAddress
      : params.outputToken.address.toViemAddress()

    // Convert slippage from basis points to decimal (e.g., 50 bps = 0.5% = 0.005)
    const slippageDecimal = (params.slippageBps / 10000).toString()

    // Convert amount to string and clean (matching old implementation)
    const amountStr = params.inputAmountRaw.toString().replace(/n$/, "")

    // Validate amount string length (matching old implementation)
    if (amountStr.length > 160) {
      throw new Error("Amount string too long")
    }

    // Build quote URL
    const quoteUrl = new URL(flyConfig.quoteAPI)
    quoteUrl.searchParams.append("network", flyConfig.network)
    quoteUrl.searchParams.append("fromTokenAddress", inputTokenAddress)
    quoteUrl.searchParams.append("toTokenAddress", outputTokenAddress)
    quoteUrl.searchParams.append("fromAddress", params.userAddress)
    quoteUrl.searchParams.append("toAddress", params.userAddress)
    quoteUrl.searchParams.append("amount", amountStr)
    quoteUrl.searchParams.append("sellAmount", amountStr)
    quoteUrl.searchParams.append("slippage", slippageDecimal)
    quoteUrl.searchParams.append("gasless", "false")

    if (FLY_AFFILIATE_FEE_IN_PERCENTAGE !== "0") {
      quoteUrl.searchParams.append("affiliateAddress", flyConfig.affiliateAddress.toViemAddress())
      quoteUrl.searchParams.append("affiliateFeeInPercentage", FLY_AFFILIATE_FEE_IN_PERCENTAGE)
    }

    // Fetch quote
    const quoteResponse = await fetch(quoteUrl, {
      mode: "cors",
      credentials: "omit",
    })

    if (!quoteResponse.ok) {
      throw new Error(`Fly quote API error: ${quoteResponse.status} ${quoteResponse.statusText}`)
    }

    const quoteData = await quoteResponse.json()

    // Validate response
    const parsedQuote = flyQuoteResponseSchema.safeParse(quoteData)
    if (!parsedQuote.success) {
      throw new Error(`Fly quote response validation failed: ${parsedQuote.error.message}`)
    }

    // Return raw response (validated but not normalized)
    return quoteData as RawAggregatorQuote
  }

  /**
   * Build transaction from Fly API
   * Returns raw response (before normalization)
   */
  async buildTransaction(quoteId: string, _params: AggregatorTransactionRequest): Promise<RawAggregatorQuote> {
    // Get chainId from quote (stored in transactionMetadata)
    const chainId = _params.quote.inputToken.chainId
    const flyConfig = getFlyChainConfig(chainId)

    // Build transaction URL
    const transactionUrl = new URL(flyConfig.transactionAPI)
    transactionUrl.searchParams.append("quoteId", quoteId)
    transactionUrl.searchParams.append("estimateGas", "false")

    // Fetch transaction data
    const transactionResponse = await fetch(transactionUrl, {
      mode: "cors",
      credentials: "omit",
      headers: {
        Accept: "application/json",
      },
    })

    if (!transactionResponse.ok) {
      const errorBody = await transactionResponse.text()
      throw new Error(
        `Fly transaction API error: ${transactionResponse.status} ${transactionResponse.statusText}${
          errorBody ? ` - ${errorBody}` : ""
        }`,
      )
    }

    const transactionData = await transactionResponse.json()

    // Validate response
    const parsedTransaction = flyTransactionResponseSchema.safeParse(transactionData)
    if (!parsedTransaction.success) {
      throw new Error(`Fly transaction response validation failed: ${parsedTransaction.error.message}`)
    }

    // Return raw response (validated but not normalized)
    return transactionData as RawAggregatorQuote
  }
}
